import { Controller } from '@hotwired/stimulus'
import { setOptions, importLibrary } from '@googlemaps/js-api-loader'
import MarkerClusterer from '@googlemaps/markerclustererplus'
import { render } from 'nunjucks'

import { router } from '../router'

import { Events, Strings } from '../constants'

import {
    target,
    retrieveSession,
    sendEvent,
    decodeJson,
    subscribeEvent,
    unsubscribeEvent,
    retrieveState,
    updateState,
    updateTmp,
    addClass,
    removeClass
} from '../utils'

import {
    myNotificationsPostRequest,
    nearNotificationsPostRequest
} from '../api'

export default class extends Controller {

    static targets = [
        'map',
        'my-location',
        'center',
    ]

    static values = {
    }

    state = {
        session: null,
        pending: [],
        visited: [],
        payed: [],
        notification: null
    }

    instances = {
        loader: null,
        map: null,
        locationWatches: null,
        myLocation: null,
        infoWindow: null,
        markers: [],
        cluster: null,
        temporalMarkers: [],
    }

    configs = {
        map: {
            key: Strings.API_KEY,
            libraries: [],
            v: 'quarterly'
        }
    }

    map = () => target(this, 'map')

    myLocation = () => target(this, 'my-location')

    center = () => target(this, 'center')

    initialize = () => {

        setOptions(this.configs.map);

        this.instances.loader = {
            load: () => importLibrary('maps')
        }
    }

    connect = () => {

        this.instances.loader.load()
            .then(() => this.renderMap())
            .then(() => this.refreshOnLogin())

        subscribeEvent(Events.ON_LOGIN, this.refreshOnLogin)
    }

    disconnect = () => {

        const { geolocation } = navigator

        if (this.instances.locationWatches) {

            geolocation.clearWatch(this.instances.locationWatches)
        }

        unsubscribeEvent(Events.ON_LOGIN, this.refreshOnLogin)
    }

    refreshOnLogin = () => {

        retrieveSession()
            .then((session) => {

                if (! session) {

                    sendEvent(Events.SHOW_LOGIN, { message: '' })

                    return
                }

                this.state.session = session

                return retrieveState()
                    .then((state) => {

                        if (! state || ! state.pending ||
                                ! state.visited || ! state.payed) {

                            this.refresh()

                            return
                        }

                        this.state.pending = state.pending
                        this.state.visited = state.visited
                        this.state.payed = state.payed

                        this.renderNotifications()
                    })
                    .catch(() => {})
          })
          .catch(() => {

              sendEvent(Events.SHOW_LOGIN, { message: '' })
          })
    }

    refresh = () => {

        if (! this.state.session) {

            return
        }

        const params = {
            rpe: this.state.session.rpe,
            password: this.state.session.password
        }

        myNotificationsPostRequest(params)
            .then(this.onRefresh)
            .catch(this.onRefresh)
    }

    onRefresh = (response) => {

        if (response.estado) {

            const {  pendientes, visitados, terminados } = decodeJson(response.mensaje)

            this.state.pending = pendientes
            this.state.visited = visitados
            this.state.payed = terminados

            this.renderNotifications()

            updateState({
                pending: this.state.pending,
                visited: this.state.visited,
                payed: this.state.payed
            })
        }
    }

    renderMap = () => {

        const { maps: { Map, InfoWindow, Marker, SymbolPath: { CIRCLE } } } = google

        this.instances.map = new Map(this.map(), {
            center: {
                lat: 21.8440139,
                lng: -102.275549
            },
            zoom: 6,
            disableDefaultUI: true,
            clickableIcons: false
        })

        this.instances.infoWindow = new InfoWindow()

        this.instances.infoWindow.addListener('closeclick',
            () => removeClass(this.center(), 'is-hidden'))


        this.instances.myLocation = new Marker({
            title: 'Mi ubicación',
            icon: {
                path: CIRCLE,
                fillColor: '#5991f8',
                fillOpacity: 0.95,
                strokeColor: '#ffffff',
                strokeOpacity: 0.95,
                strokeWeight: 2,
                scale: 15
            }
        })
    }

    renderNotifications = () => {

        const { maps: { Marker, LatLng, SymbolPath: { BACKWARD_CLOSED_ARROW } } } = google

        removeClass(this.center(), 'is-hidden')

        this.instances.infoWindow.close()

        this.state.pending.forEach((notification) => {

            const marker = new Marker({
                position: new LatLng(parseFloat(notification.x), parseFloat(notification.y)),
                title: notification.notificacion,
                icon: {
                    path: BACKWARD_CLOSED_ARROW,
                    fillColor: '#a30101',
                    fillOpacity: 1.0,
                    strokeColor: '#3f0000',
                    strokeOpacity: 1.0,
                    strokeWeight: 1,
                    scale: 6
                }
            })

            const callback = this.markerClicked.bind(this, notification, marker)

            marker.addListener('click', callback)

            this.instances.markers.push(marker)
        })

        this.state.visited.forEach((notification) => {

            const marker = new Marker({
                position: new LatLng(parseFloat(notification.x), parseFloat(notification.y)),
                title: notification.notificacion,
                icon: {
                    path: BACKWARD_CLOSED_ARROW,
                    fillColor: '#013aa3',
                    fillOpacity: 1.0,
                    strokeColor: '#000a3f',
                    strokeOpacity: 1.0,
                    strokeWeight: 1,
                    scale: 6
                }
            })

            const callback = this.markerClicked.bind(this, notification, marker, false)

            marker.addListener('click', callback)

            this.instances.markers.push(marker)
        })

        this.instances.cluster = new MarkerClusterer(this.instances.map, this.instances.markers, {
            clusterClass: 'custom-clustericon',
            styles: [
                {
                    width: 20,
                    height: 20,
                    className: 'custom-clustericon-1'
                },
                {
                    width: 30,
                    height: 30,
                    className: 'custom-clustericon-2'
                },
                {
                    width: 40,
                    height: 40,
                    className: 'custom-clustericon-3'
                }
            ]
        })
    }

    markerClicked = (notification, marker, isTemporal) => {

        const content = render('window-info-notification.html', notification)

        this.instances.infoWindow.setContent(content)

        this.instances.infoWindow.setPosition(marker.getPosition())

        this.instances.infoWindow.open(this.instances.map)

        this.state.notification = notification

        addClass(this.center(), 'is-hidden')
    }

    openMaps = ({ params }) => {

        if (isNaN(params.x) || isNaN(params.y)) {

            return
        }

        const platform = (navigator.userAgentData || navigator).platform || 'unknown'

        if (platform.indexOf('iPhone') !== -1 ||
                platform.indexOf('iPod') !== -1 ||
                    platform.indexOf('iPad') !== -1) {

            window.open(`maps://maps.google.com/maps?daddr=${params.x},${params.y}`)

        } else {

            window.open(`https://maps.google.com/maps?daddr=${params.x},${params.y}`)
        }
    }

    checkLocation = () => {

        const { geolocation } = navigator

        if (! geolocation) {

            this.errorLocation(false)

            return
        }

        addClass(this.myLocation(), 'is-loading')

        const options = {
            enableHighAccuracy: true,
            timeout: 30000,
            maximumAge: 60000
        }

        geolocation.getCurrentPosition(this.updateLocation, this.errorUpdateLocation, options)

        if (this.instances.locationWatches) {

            geolocation.clearWatch(this.instances.locationWatches)

            this.instances.locationWatches = null
        }

        this.instances.locationWatches = geolocation.watchPosition(
          this.updateLocationInBackground, this.errorLocationInBackground, options)
    }

    updateLocation = ({ coords: { latitude, longitude } }) => {

        const { maps: { LatLng } } = google

        this.instances.map.panTo(new LatLng(latitude, longitude))

        this.instances.map.setZoom(16)

        removeClass(this.myLocation(), 'is-loading')
    }

    updateLocationInBackground = ({ coords: { latitude, longitude } }) => {

        const { maps: { LatLng } } = google

        this.instances.myLocation.setMap(this.instances.map)

        this.instances.myLocation.setPosition(new LatLng(latitude, longitude))
    }

    errorUpdateLocation = (error) => {

        this.errorLocation(true, error.code !== 1)
    }

    errorLocation = (browserHasGeolocation, hasPermissions) => {

        const position = this.instances.map.getCenter()

        this.instances.infoWindow.setPosition(position)

        this.instances.infoWindow.setContent(browserHasGeolocation ? (hasPermissions ?
            Strings.ERROR_LOCATION : Strings.ERROR_LOCATION_PERMISSION)  : Strings.ERROR_LOCATION_FEATURE)

        this.instances.infoWindow.open(this.instances.map)

        addClass(this.center(), 'is-hidden')

        removeClass(this.myLocation(), 'is-loading')
    }

    errorLocationInBackground = () => {
    }

    nearNotifications = () => {

        const center = this.instances.map.getCenter()

        const params = {
            rpe: this.state.session.rpe,
            password: this.state.session.password,
            latitud: center.lat(),
            longitud: center.lng()
        }

        nearNotificationsPostRequest(params)
            .then(this.onNearNotifications)
            .catch(this.onNearNotifications)
    }

    onNearNotifications = (response) => {

        const center = this.instances.map.getCenter()

        if (response.estado) {

            const notifications = decodeJson(response.mensaje)

            const { maps: { Marker, LatLng, SymbolPath: { BACKWARD_CLOSED_ARROW, BACKWARD_OPEN_ARROW } } } = google

            removeClass(this.center(), 'is-hidden')

            this.instances.infoWindow.close()

            this.instances.temporalMarkers.forEach((marker) => {

                marker.setMap(null)
            })

            notifications.forEach((notification) => {

                const marker = new Marker({
                    map: this.instances.map,
                    position: new LatLng(parseFloat(notification.x), parseFloat(notification.y)),
                    title: notification.notificacion,
                    icon: {
                        path: BACKWARD_CLOSED_ARROW,
                        fillColor: '#9f0303',
                        fillOpacity: 1.0,
                        strokeColor: '#d00000',
                        strokeOpacity: 1.0,
                        strokeWeight: 1,
                        scale: 6
                    }
                })

                const callback = this.markerClicked.bind(this, notification, marker, true)

                marker.addListener('click', callback)

                this.instances.temporalMarkers.push(marker)
            })

            const marker = new Marker({
                map: this.instances.map,
                position: new LatLng(center.lat(), center.lng()),
                title: 'Punto de Búsqueda',
                icon: {
                    path: BACKWARD_OPEN_ARROW,
                    fillColor: '#000000',
                    fillOpacity: 1.0,
                    strokeColor: '#1f4119',
                    strokeOpacity: 1.0,
                    strokeWeight: 1,
                    scale: 8
                }
            })

            this.instances.temporalMarkers.push(marker)
        }
    }

    notification = ({ params: { notification } }) => {

        if (notification.toString() === this.state.notification.notificacion.toString()) {

            updateTmp(this.state.notification)
                .then(() => {

                    router.navigate(`/notificacion/${notification}`, {
                        historyAPIMethod: 'replaceState'
                    })
                })
                .catch(() => {})
        }
    }
}
